import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface TableHeaderConfigData {
  value: string;
  description: string;
  order: number;
  visible: boolean;
  fixedPosition: 'start' | 'end' | 'both' | null;
}

@Component({
  selector: 'app-table-headers-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatDialogModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './table-headers-modal.component.html',
})
export class TableHeadersModalComponent {
  public headerOptions: TableHeaderConfigData[] = [];

  constructor(
    public dialogRef: MatDialogRef<TableHeadersModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { headers: TableHeaderConfigData[] },
  ) {
    this.headerOptions = (this.data.headers ?? [])
      .map((header, index) => ({
        value: header.value,
        description: header.description,
        visible: header.visible ?? true,
        order: header.order ?? index + 1,
        fixedPosition: header.fixedPosition ?? null,
      }))
      .sort((left, right) => left.order - right.order)
      .map((header, index) => ({
        ...header,
        visible: this.isLocked(header) ? true : header.visible,
        order: index + 1,
      }));
  }

  public drop(event: CdkDragDrop<TableHeaderConfigData[]>): void {
    const movingHeader = this.headerOptions[event.previousIndex];

    if (!movingHeader || this.isLocked(movingHeader)) {
      return;
    }

    const minIndex = this.leadingLockedCount();
    const maxIndex = this.headerOptions.length - this.trailingLockedCount() - 1;
    const currentIndex = Math.min(maxIndex, Math.max(minIndex, event.currentIndex));

    moveItemInArray(this.headerOptions, event.previousIndex, currentIndex);
    this.reassignOrder();
  }

  public isLocked(header: TableHeaderConfigData): boolean {
    return !!header.fixedPosition;
  }

  public lockLabel(header: TableHeaderConfigData): string {
    if (header.fixedPosition === 'both') {
      return 'Fija al inicio y al final';
    }

    if (header.fixedPosition === 'start') {
      return 'Fija al inicio';
    }

    if (header.fixedPosition === 'end') {
      return 'Fija al final';
    }

    return '';
  }

  public closeModal(): void {
    this.dialogRef.close(null);
  }

  public save(): void {
    this.dialogRef.close(this.headerOptions);
  }

  private reassignOrder(): void {
    this.headerOptions = this.headerOptions.map((header, index) => ({
      ...header,
      visible: this.isLocked(header) ? true : header.visible,
      order: index + 1,
    }));
  }

  private leadingLockedCount(): number {
    return this.headerOptions.filter(
      (header) => header.fixedPosition === 'start' || header.fixedPosition === 'both',
    ).length;
  }

  private trailingLockedCount(): number {
    return this.headerOptions.filter((header) => header.fixedPosition === 'end').length;
  }
}
